import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './DJ.css';
import api from './api';
import LanguageSwitcher from './components/LanguageSwitcher';
import ThemeToggle from './components/ThemeToggle';

const GOOGLE_CLIENT_ID = '525994361594-mtk660o3tiqp68mbang8uncbtp9s3dqg.apps.googleusercontent.com';

function DJ() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [signups, setSignups] = useState([]);
  const [playlist, setPlaylist] = useState([]);
  const [played, setPlayed] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropIndex, setDropIndex] = useState(null); // Index where item will be inserted
  const [playedExpanded, setPlayedExpanded] = useState(false);
  const [deletedExpanded, setDeletedExpanded] = useState(false);
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [copySingerMode, setCopySingerMode] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [danceBreakMode, setDanceBreakMode] = useState(false); // false = add dance break, true = add resume
  const playlistRef = useRef(null);
  const isMobile = window.innerWidth <= 768;

  // Check if user is already authenticated (from localStorage)
  useEffect(() => {
    const storedAuth = localStorage.getItem('dj_auth');
    if (storedAuth) {
      try {
        const auth = JSON.parse(storedAuth);
        // Check if token is still valid (expires after 24 hours)
        if (auth.timestamp && Date.now() - auth.timestamp < 24 * 60 * 60 * 1000) {
          setIsAuthenticated(true);
          setAuthUser(auth);
        } else {
          localStorage.removeItem('dj_auth');
        }
      } catch (e) {
        localStorage.removeItem('dj_auth');
      }
    }
    setAuthLoading(false);
  }, []);

  // Load Google Sign-In script
  useEffect(() => {
    if (isAuthenticated) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback
      });
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        { theme: 'filled_blue', size: 'large', width: 280 }
      );
    };

    return () => {
      document.body.removeChild(script);
    };
  }, [isAuthenticated, authLoading]);

  const handleGoogleCallback = async (response) => {
    try {
      setAuthError('');
      const result = await api.post('/api/auth/google', {
        credential: response.credential
      });

      if (result.data.success) {
        const authData = {
          email: result.data.email,
          name: result.data.name,
          picture: result.data.picture,
          timestamp: Date.now()
        };
        localStorage.setItem('dj_auth', JSON.stringify(authData));
        setAuthUser(authData);
        setIsAuthenticated(true);
      }
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Authentication failed');
    }
  };

  const handleLogout = () => {
    const email = authUser?.email;
    localStorage.removeItem('dj_auth');

    // Revoke Google token, then reload so the sign-in button renders fresh
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
      if (email) {
        window.google.accounts.id.revoke(email, () => {
          window.location.reload();
        });
        return;
      }
    }

    window.location.reload();
  };

  // Helper to strip non-Latin characters (for copy)
  const stripNonLatin = (str) => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^\x00-\x7F]/g, ''); // Remove non-ASCII
  };

  // Search filter function
  const filterBySearch = (items) => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase().trim();
    return items.filter(item => {
      const title = (item.title || '').toLowerCase();
      const artist = (item.artist || '').toLowerCase();
      const singer = (item.singer_name || '').toLowerCase();
      return title.includes(query) || artist.includes(query) || singer.includes(query);
    });
  };

  const loadData = useCallback(async () => {
    try {
      const [signupsRes, playlistRes, statsRes, playedRes, deletedRes] = await Promise.all([
        api.get('/api/signups'),
        api.get('/api/playlist'),
        api.get('/api/stats'),
        api.get('/api/played'),
        api.get('/api/deleted')
      ]);
      setSignups(signupsRes.data || []);
      setPlaylist(playlistRes.data || []);
      setStats(statsRes.data);
      setPlayed(playedRes.data || []);
      setDeleted(deletedRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 3000);
    return () => clearInterval(interval);
  }, [loadData]);

  const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date();
    // If it's already ISO format (contains 'T' or 'Z'), parse directly
    if (timestamp.includes('T') || timestamp.includes('Z')) {
      return new Date(timestamp);
    }
    // SQLite format without timezone - treat as UTC
    return new Date(timestamp.replace(' ', 'T') + 'Z');
  };

  const formatWaitTime = (timestamp) => {
    const now = new Date();
    const added = parseTimestamp(timestamp);
    const diffMs = now - added;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffSecs < 60) return `${diffSecs}s`;
    if (diffMins < 60) return `${diffMins}m`;
    const remainingMins = diffMins % 60;
    if (remainingMins === 0) return `${diffHours}h`;
    return `${diffHours}h ${remainingMins}m`;
  };

  const formatTime = (timestamp) => {
    const date = parseTimestamp(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (duration) => {
    if (!duration) return '';
    // If already formatted as MM:SS, return as is
    if (duration.includes(':')) return duration;
    // Otherwise it's seconds, convert to MM:SS
    const secs = parseInt(duration, 10);
    if (isNaN(secs)) return duration;
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  // Drag handlers
  const handleDragStart = (e, item, source) => {
    setDraggedItem({ item, source });
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight delay to allow the drag image to be created before styling
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedItem(null);
    setDropIndex(null);
  };

  // Calculate drop index based on mouse position
  const handlePlaylistDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedItem || !playlistRef.current) return;

    const items = playlistRef.current.querySelectorAll('.playlist-row');

    if (items.length === 0) {
      setDropIndex(0);
      return;
    }

    const mouseY = e.clientY;
    let newDropIndex = playlist.length; // Default to end

    for (let i = 0; i < items.length; i++) {
      const itemRect = items[i].getBoundingClientRect();
      const itemMiddle = itemRect.top + itemRect.height / 2;

      if (mouseY < itemMiddle) {
        newDropIndex = i;
        break;
      }
    }

    // If dragging within playlist, adjust index to account for the dragged item
    if (draggedItem.source === 'playlist') {
      const draggedIndex = playlist.findIndex(p => p.id === draggedItem.item.id);
      // Don't show indicator at current position or adjacent position (would result in no change)
      if (newDropIndex === draggedIndex || newDropIndex === draggedIndex + 1) {
        setDropIndex(null);
        return;
      }
    }

    setDropIndex(newDropIndex);
  };

  const handlePlaylistDragLeave = (e) => {
    // Only clear if leaving the playlist container entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropIndex(null);
    }
  };

  const handlePlaylistDrop = async (e) => {
    e.preventDefault();

    if (!draggedItem || dropIndex === null) {
      setDraggedItem(null);
      setDropIndex(null);
      return;
    }

    const { item, source } = draggedItem;

    try {
      if (source === 'signups') {
        // Add to playlist at specific position
        await api.post(`/api/playlist/add/${item.id}`, { position: dropIndex + 1 });
      } else if (source === 'playlist') {
        // Reorder within playlist
        const currentIndex = playlist.findIndex(p => p.id === item.id);

        if (currentIndex === -1) return;

        const newPlaylist = [...playlist];
        newPlaylist.splice(currentIndex, 1);

        // Adjust insert index if we removed an item before it
        let insertIndex = dropIndex;
        if (currentIndex < dropIndex) {
          insertIndex = dropIndex - 1;
        }

        newPlaylist.splice(insertIndex, 0, item);

        const orderedIds = newPlaylist.map(p => p.id);
        await api.post('/api/playlist/reorder', { orderedIds });
      }
      await loadData();
    } catch (err) {
      showMessage(t('dj.messages.failedUpdatePlaylist'), 'error');
    }

    setDraggedItem(null);
    setDropIndex(null);
  };

  // Arrow button handlers for reordering
  const handleMoveUp = async (index) => {
    if (index === 0) return;

    const newPlaylist = [...playlist];
    [newPlaylist[index - 1], newPlaylist[index]] = [newPlaylist[index], newPlaylist[index - 1]];

    const orderedIds = newPlaylist.map(p => p.id);
    try {
      await api.post('/api/playlist/reorder', { orderedIds });
      await loadData();
    } catch (err) {
      showMessage(t('dj.messages.failedReorder'), 'error');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === playlist.length - 1) return;

    const newPlaylist = [...playlist];
    [newPlaylist[index], newPlaylist[index + 1]] = [newPlaylist[index + 1], newPlaylist[index]];

    const orderedIds = newPlaylist.map(p => p.id);
    try {
      await api.post('/api/playlist/reorder', { orderedIds });
      await loadData();
    } catch (err) {
      showMessage(t('dj.messages.failedReorder'), 'error');
    }
  };

  const handleRemoveFromPlaylist = async (id) => {
    try {
      await api.post(`/api/playlist/remove/${id}`);
      await loadData();
    } catch (err) {
      showMessage(t('dj.messages.failedRemovePlaylist'), 'error');
    }
  };

  const handleMarkAsPlayed = async (id) => {
    try {
      await api.post(`/api/queue/${id}/played`);
      await loadData();
      showMessage(t('dj.messages.markedPlayed'), 'success');
    } catch (err) {
      showMessage(err.response?.data?.error || t('dj.messages.failedMarkPlayed'), 'error');
    }
  };

  const handleRemoveFromQueue = async (id) => {
    try {
      await api.post(`/api/queue/${id}/delete`);
      await loadData();
      showMessage(t('dj.messages.removedFromQueue'), 'success');
    } catch (err) {
      showMessage(t('dj.messages.failedRemoveQueue'), 'error');
    }
  };

  const handleAddToPlaylist = async (id) => {
    try {
      await api.post(`/api/playlist/add/${id}`);
      await loadData();
    } catch (err) {
      showMessage(t('dj.messages.failedAddPlaylist'), 'error');
    }
  };

  const handleCopyToggle = async (item) => {
    const isInSingerMode = copySingerMode[item.id];
    const rawText = isInSingerMode ? item.singer_name : `${item.artist} - ${item.title}`;
    const textToCopy = stripNonLatin(rawText);

    try {
      await navigator.clipboard.writeText(textToCopy);
      showMessage(t('dj.messages.copiedClipboard'), 'success');
      // Toggle the mode for this item
      setCopySingerMode(prev => ({
        ...prev,
        [item.id]: !isInSingerMode
      }));
    } catch (err) {
      showMessage(t('dj.messages.failedCopy'), 'error');
    }
  };

  const handleDanceBreakToggle = async () => {
    const type = danceBreakMode ? 'resume_karaoke' : 'dance_break';
    try {
      await api.post('/api/playlist/placeholder', { type });
      setDanceBreakMode(!danceBreakMode);
      await loadData();
      showMessage(danceBreakMode ? t('dj.resumeKaraoke') : t('dj.danceBreak'), 'success');
    } catch (err) {
      showMessage('Failed to add placeholder', 'error');
    }
  };

  const handleRestoreDeleted = async (id, to) => {
    try {
      await api.post(`/api/restore/deleted/${id}`, { to });
      await loadData();
      showMessage(t('dj.messages.restored'), 'success');
    } catch (err) {
      showMessage(t('dj.messages.failedRestore'), 'error');
    }
  };

  const handleRestorePlayed = async (id, to) => {
    try {
      await api.post(`/api/restore/played/${id}`, { to });
      await loadData();
      showMessage(t('dj.messages.restored'), 'success');
    } catch (err) {
      showMessage(t('dj.messages.failedRestore'), 'error');
    }
  };

  const handleGeneratePlaylist = async () => {
    if (playlist.length === 0) {
      showMessage(t('dj.messages.playlistEmpty'), 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/api/playlist/generate');
      const { count } = response.data;
      showMessage(t('dj.messages.playlistGenerated', { count }), 'success');
    } catch (err) {
      showMessage(err.response?.data?.error || t('dj.messages.failedGeneratePlaylist'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNewSessionClick = () => {
    setShowNewSessionModal(true);
  };

  const handleNewSessionConfirm = async () => {
    setShowNewSessionModal(false);
    try {
      await api.post('/api/session/new');
      await loadData();
      showMessage(t('dj.messages.newSessionStarted'), 'success');
    } catch (err) {
      showMessage(t('dj.messages.failedNewSession'), 'error');
    }
  };

  const handleNewSessionCancel = () => {
    setShowNewSessionModal(false);
  };

  const showMessage = (msg, type) => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(''), 3000);
  };

  // Render a drop indicator line
  const renderDropIndicator = () => (
    <div className="drop-indicator">
      <div className="drop-indicator-line"></div>
    </div>
  );

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="dj-app">
        <div className="dj-auth-screen">
          <div className="dj-auth-loading">Loading...</div>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="dj-app">
        <div className="dj-auth-screen">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Karaoke Imprezy" className="dj-auth-logo" />
          <h1>DJ Panel</h1>
          <p className="dj-auth-subtitle">Sign in to access the DJ control panel</p>
          <div id="google-signin-button"></div>
          {authError && <p className="dj-auth-error">{authError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="dj-app">
      {/* Confirmation Modal */}
      {showNewSessionModal && (
        <div className="modal-overlay" onClick={handleNewSessionCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('dj.newSession')}</h3>
            <p>{t('dj.confirmNewSession')}</p>
            <div className="modal-actions">
              <button onClick={handleNewSessionCancel} className="btn-modal-cancel">
                {t('dj.cancel')}
              </button>
              <button onClick={handleNewSessionConfirm} className="btn-modal-confirm">
                {t('dj.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="dj-header">
        <div className="dj-header-left desktop-only">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Karaoke Imprezy" className="dj-header-logo" />
          <ThemeToggle />
          <button onClick={handleNewSessionClick} className="btn-new-session-header">
            {t('dj.newSession')}
          </button>
          <button
            onClick={handleDanceBreakToggle}
            className={`btn-dance-break ${danceBreakMode ? 'resume-mode' : ''}`}
          >
            {danceBreakMode ? t('dj.resumeKaraoke') : t('dj.danceBreak')}
          </button>
          <button onClick={handleLogout} className="btn-logout" title={`Logged in as ${authUser?.email}`}>
            Logout
          </button>
        </div>
        <div className="dj-header-mobile-left">
          <button onClick={handleNewSessionClick} className="btn-new-session-header">
            {t('dj.newSessionShort')}
          </button>
          <ThemeToggle />
          <button
            onClick={handleDanceBreakToggle}
            className={`btn-dance-break-mobile ${danceBreakMode ? 'resume-mode' : ''}`}
            title={danceBreakMode ? t('dj.resumeKaraoke') : t('dj.danceBreak')}
          >
            {danceBreakMode ? '🎤' : '💃'}
          </button>
        </div>
        <h1>{t('dj.title')}</h1>
        <LanguageSwitcher />
      </header>

      <div className="dj-search-bar">
        <input
          type="text"
          className="dj-search-input"
          placeholder={t('dj.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="dj-search-clear" onClick={() => setSearchQuery('')}>
            ✕
          </button>
        )}
      </div>

      <div className="dj-content">
        {message && (
          <div className={`dj-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="dj-panels">
          {/* Sign-ups Panel */}
          <div className="dj-panel signups-panel">
            <h2>{t('dj.signups')} ({signups.length})</h2>
            <p className="panel-hint">{t('dj.dragToPlaylist')}</p>

            {signups.length === 0 ? (
              <div className="empty-panel">{t('dj.noSignups')}</div>
            ) : (
              <div className="panel-list">
                {filterBySearch(signups).map((item) => (
                  <div
                    key={item.id}
                    className="panel-row"
                    draggable={!isMobile}
                    onDragStart={(e) => !isMobile && handleDragStart(e, item, 'signups')}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="row-drag-handle desktop-only">⋮⋮</div>
                    <div className="row-info">
                      <div className="row-song">
                        <strong>{item.title}</strong> - {item.artist}
                        <span className={`source-badge ${item.source}`}>
                          {item.source === 'karafun' ? 'KF' : 'iS'}
                        </span>
                      </div>
                      <div className="row-meta">
                        <span className="row-singer">{item.singer_name}</span>
                        {item.tonation !== 0 && (
                          <span className={`tonation-badge ${item.tonation > 0 ? 'positive' : 'negative'}`}>
                            {item.tonation > 0 ? `+${item.tonation}` : item.tonation}
                          </span>
                        )}
                        <span className="row-wait" title={`${t('dj.addedAt')} ${formatTime(item.added_at)}`}>
                          {t('dj.waiting')}: {formatWaitTime(item.added_at)}
                        </span>
                      </div>
                    </div>
                    <div className="row-actions">
                      <button
                        onClick={() => handleAddToPlaylist(item.id)}
                        className="btn-add-playlist"
                        title="Add to playlist"
                      >
                        →
                      </button>
                      <button
                        onClick={() => handleRemoveFromQueue(item.id)}
                        className="btn-remove"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Playlist Panel */}
          <div className="dj-panel playlist-panel">
            <h2>{t('dj.playlist')} ({playlist.length})</h2>
            <p className="panel-hint">{t('dj.dragToReorder')}</p>

            <div
              ref={playlistRef}
              className={`panel-list playlist-drop-zone ${draggedItem ? 'drag-active' : ''}`}
              onDragOver={handlePlaylistDragOver}
              onDragLeave={handlePlaylistDragLeave}
              onDrop={handlePlaylistDrop}
            >
              {playlist.length === 0 && !draggedItem ? (
                <div className="empty-panel">
                  {t('dj.dragSongsHere')}
                </div>
              ) : playlist.length === 0 && draggedItem ? (
                <div className="empty-panel drop-hint">
                  {dropIndex !== null && renderDropIndicator()}
                  {t('dj.dropToAdd')}
                </div>
              ) : (
                <>
                  {dropIndex === 0 && renderDropIndicator()}
                  {filterBySearch(playlist).map((item, index) => {
                    const isBeingDragged = draggedItem?.item.id === item.id && draggedItem?.source === 'playlist';
                    const showIndicatorAfter = dropIndex === index + 1;
                    const isPlaceholder = item.is_placeholder;
                    const placeholderClass = isPlaceholder ? `placeholder-row ${item.placeholder_type}` : '';

                    return (
                      <React.Fragment key={item.id}>
                        <div
                          className={`panel-row playlist-row ${isBeingDragged ? 'dragging' : ''} ${placeholderClass}`}
                          draggable={!isMobile}
                          onDragStart={(e) => !isMobile && handleDragStart(e, item, 'playlist')}
                          onDragEnd={handleDragEnd}
                        >
                          <div className="row-position">{index + 1}</div>
                          <div className="row-arrows">
                            <button
                              onClick={() => handleMoveUp(index)}
                              className="btn-arrow"
                              disabled={index === 0}
                              title="Move up"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => handleMoveDown(index)}
                              className="btn-arrow"
                              disabled={index === playlist.length - 1}
                              title="Move down"
                            >
                              ▼
                            </button>
                          </div>
                          <div className="row-drag-handle desktop-only">⋮⋮</div>
                          <div className="row-info">
                            {isPlaceholder ? (
                              <div className="row-song placeholder-title">
                                <span className="placeholder-emoji">
                                  {item.placeholder_type === 'dance_break' ? '💃🕺' : '🎤🎶'}
                                </span>
                                <strong>{item.placeholder_type === 'dance_break' ? t('dj.danceBreakTitle') : t('dj.resumeKaraokeTitle')}</strong>
                              </div>
                            ) : (
                              <>
                                <div className="row-song">
                                  <strong>{item.title}</strong> - {item.artist}
                                  <span className={`source-badge ${item.source}`}>
                                    {item.source === 'karafun' ? 'KF' : 'iS'}
                                  </span>
                                </div>
                                <div className="row-meta">
                                  <span className="row-singer">{item.singer_name}</span>
                                  {item.tonation !== 0 && (
                                    <span className={`tonation-badge ${item.tonation > 0 ? 'positive' : 'negative'}`}>
                                      {item.tonation > 0 ? `+${item.tonation}` : item.tonation}
                                    </span>
                                  )}
                                  <span className="row-wait" title={`${t('dj.addedAt')} ${formatTime(item.added_at)}`}>
                                    {formatWaitTime(item.added_at)}
                                  </span>
                                  <span className="row-duration">{formatDuration(item.duration)}</span>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="row-actions">
                            {!isPlaceholder && (
                              <>
                                <button
                                  onClick={() => handleCopyToggle(item)}
                                  className={`btn-copy desktop-only ${copySingerMode[item.id] ? 'singer-mode' : ''}`}
                                  title={copySingerMode[item.id] ? "Copy singer name" : "Copy song name"}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleMarkAsPlayed(item.id)}
                                  className="btn-played"
                                  title="Mark as played"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => handleRemoveFromPlaylist(item.id)}
                                  className="btn-back"
                                  title="Back to sign-ups"
                                >
                                  ←
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleRemoveFromQueue(item.id)}
                              className="btn-remove"
                              title="Remove"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                        {showIndicatorAfter && renderDropIndicator()}
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Collapsible Played Panel */}
        <div className="collapsible-panel">
          <button
            className="collapsible-header"
            onClick={() => setPlayedExpanded(!playedExpanded)}
          >
            <span className={`collapse-arrow ${playedExpanded ? 'expanded' : ''}`}>▶</span>
            {t('dj.played')} ({played.length})
          </button>
          {playedExpanded && (
            <div className="collapsible-content">
              {played.length === 0 ? (
                <div className="empty-collapsible">{t('dj.noPlayedYet')}</div>
              ) : (
                <div className="collapsible-list">
                  {filterBySearch(played).map((item) => (
                    <div key={item.id} className="collapsible-row">
                      <div className="row-info">
                        <div className="row-song">
                          <strong>{item.title}</strong> - {item.artist}
                          <span className={`source-badge ${item.source}`}>
                            {item.source === 'karafun' ? 'KF' : 'iS'}
                          </span>
                        </div>
                        <div className="row-meta">
                          <span className="row-singer">{item.singer_name}</span>
                          {item.tonation !== 0 && (
                            <span className={`tonation-badge ${item.tonation > 0 ? 'positive' : 'negative'}`}>
                              {item.tonation > 0 ? `+${item.tonation}` : item.tonation}
                            </span>
                          )}
                          <span className="row-duration">{formatDuration(item.duration)}</span>
                        </div>
                      </div>
                      <div className="row-actions">
                        <button
                          onClick={() => handleRestorePlayed(item.id, 'signups')}
                          className="btn-restore"
                          title={t('dj.restoreToSignups')}
                        >
                          ↩
                        </button>
                        <button
                          onClick={() => handleRestorePlayed(item.id, 'playlist')}
                          className="btn-restore-playlist"
                          title={t('dj.restoreToPlaylist')}
                        >
                          ↪
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Collapsible Deleted Panel */}
        <div className="collapsible-panel">
          <button
            className="collapsible-header"
            onClick={() => setDeletedExpanded(!deletedExpanded)}
          >
            <span className={`collapse-arrow ${deletedExpanded ? 'expanded' : ''}`}>▶</span>
            {t('dj.deleted')} ({deleted.length})
          </button>
          {deletedExpanded && (
            <div className="collapsible-content">
              {deleted.length === 0 ? (
                <div className="empty-collapsible">{t('dj.noDeletedYet')}</div>
              ) : (
                <div className="collapsible-list">
                  {filterBySearch(deleted).map((item) => (
                    <div key={item.id} className="collapsible-row">
                      <div className="row-info">
                        <div className="row-song">
                          <strong>{item.title}</strong> - {item.artist}
                          <span className={`source-badge ${item.source}`}>
                            {item.source === 'karafun' ? 'KF' : 'iS'}
                          </span>
                        </div>
                        <div className="row-meta">
                          <span className="row-singer">{item.singer_name}</span>
                          {item.tonation !== 0 && (
                            <span className={`tonation-badge ${item.tonation > 0 ? 'positive' : 'negative'}`}>
                              {item.tonation > 0 ? `+${item.tonation}` : item.tonation}
                            </span>
                          )}
                          <span className="row-duration">{formatDuration(item.duration)}</span>
                        </div>
                      </div>
                      <div className="row-actions">
                        <button
                          onClick={() => handleRestoreDeleted(item.id, 'signups')}
                          className="btn-restore"
                          title={t('dj.restoreToSignups')}
                        >
                          ↩
                        </button>
                        <button
                          onClick={() => handleRestoreDeleted(item.id, 'playlist')}
                          className="btn-restore-playlist"
                          title={t('dj.restoreToPlaylist')}
                        >
                          ↪
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DJ;
